/**
 * Typed calls into the Two Plates service (#1312).
 *
 * One module, so a route path is written once. Every shape here mirrors what
 * src/routes/household.ts actually returns — including the distinction the
 * screens depend on: `answers` is what the eater has said, `profile` is every
 * group with silence resolved to will-eat.
 */
import { api } from "../lib/api";

export interface FoodGroup {
  id: number;
  key: string;
  name: string;
  examples: string | null;
}

export interface TastePreset {
  key: string;
  name: string;
  note: string;
  answers: Record<string, number>;
}

export interface Restriction {
  id: number;
  target: string;
  severity: "avoid" | "medical" | "allergy";
  note: string | null;
  /** Since when (#1308). "We stopped eating pork in March" is a real fact. */
  effectiveFrom?: string;
}

export interface AffinityChange {
  group: string;
  from: number | null;
  to: number;
  source: "stated" | "suggested";
  changedAt: string;
}

export interface ItemPreference {
  itemName: string;
  brand: string | null;
  size: string | null;
  starred: boolean;
  /** "" means everywhere. */
  retailer: string;
  note: string | null;
}

export interface EaterInviteStatus {
  sent: true;
  openedAt: string | null;
  answeredAt: string | null;
  expiresAt: string;
}

export interface Eater {
  id: number;
  name: string;
  colour: string;
  leftoverTolerance: number;
  restrictions: Restriction[];
  invite: EaterInviteStatus | null;
  /** What they have actually answered. An absent key means not yet asked. */
  answers: Record<string, number>;
  /** Every group, silence resolved to 1 (will eat). What the engine reads. */
  profile: Record<string, number>;
  unanswered: string[];
  /** Most recent changes to what they said (#1308). */
  history?: AffinityChange[];
}

export interface Household {
  id: number;
  name: string;
  shopDay: number;
  prefersStyle: "pack" | "scratch";
  varietyPreference: "repeat" | "balanced" | "varied";
  /** 3 to 7. Three is the default because it is how long fresh food lasts. */
  planDays: number;
  /** IANA zone. Anything the SERVER formats uses this (#1331). */
  timezone: string;
  eaters: Eater[];
}

export interface RestrictionTarget {
  key: string;
  name: string;
  kind: "ingredient" | "group";
  hint: string;
}

export interface AuditQuestion {
  id: number;
  itemName: string;
  storedIn: string;
  aisle: string;
  forMeals: string[];
  recheckDays: number | null;
}

export interface Audit {
  planId: number;
  totalItems: number;
  ask: AuditQuestion[];
  skipped: { perishable: number; recentlyAnswered: number };
}

/** What the invite holder sees. Deliberately nothing about the household. */
export interface LinkView {
  eater: { name: string; leftoverTolerance: number; answers: Record<string, number>; unanswered: string[] };
  groups: FoodGroup[];
  presets: TastePreset[];
  answeredAt: string | null;
}


export interface Plate {
  eaterId: number;
  eaterName: string;
  /** Fork labels, best first, at most two. Instructions, not nouns. */
  forks: string[];
  /** Sides this eater will not eat, named so the card can say so. */
  skippedSides: string[];
  baseOnly: boolean;
}

export interface OfferedMeal {
  mealId: number;
  name: string;
  /** Hands-on time. Never total — "480 minutes" is a lie about a slow cooker. */
  activeMinutes: number;
  unattendedMinutes: number;
  slowCooker: boolean;
  makesLunch: boolean;
  score: number;
  anchor: string;
  /** Only what is actually being made. A side this household cannot have is gone. */
  sides: string[];
  /** Sides dropped for this household, with the reason (#1322). */
  droppedSides: { name: string; reason: string }[];
  plates: Plate[];
  balance: { tag: "protein" | "veg" | "grain" | "fruit"; covered: boolean }[];
  styleNotes: string[];
  /** Why this card sits where it does in the deck (#1325). */
  varietyNote: string | null;
  overlapWith: string | null;
  /** "We cook this" (#1329). */
  starred: boolean;
  /** Written after cooking it last time. */
  note: string | null;
  /** What this household's own dinners said about it (#1284). */
  cookScore: number;
  timesCooked: number;
}

export interface DroppedMeal {
  mealId: number;
  name: string;
  kind: "allergy" | "medical" | "taste" | "unserveable";
  reason: string;
}

export interface HouseholdSettings {
  /** C | F — how this household's oven is marked. */
  temperatureUnit: "C" | "F";
  /** Whether the recipe bank may learn from what this household uploads. */
  learnFromRecipes: boolean;
}

export interface Deck {
  varietyPreference: "repeat" | "balanced" | "varied";
  household: { id: number; name: string; prefersStyle: "pack" | "scratch"; planDays: number };
  eaters: { id: number; name: string }[];
  offered: OfferedMeal[];
  /**
   * ⚠️ Kept OUT of `offered` on purpose. The deck is a dinner chooser — three
   * swipes fill three days — and mixing breakfasts into that queue would break
   * it. The cookbook shows them; the deck ignores them.
   */
  breakfasts: BreakfastMeal[];
  dropped: DroppedMeal[];
}

export interface BreakfastMeal {
  mealId: number;
  name: string;
  activeMinutes: number;
  anchor: string;
  sides: string[];
}


/**
 * What one kitchen found, against what the library guessed.
 *
 * The library's numbers are authored estimates — how long a meal takes, whether
 * it reheats, whether the split survives a real table. These are the corrections.
 */
export interface MealMarkInput {
  starred?: boolean;
  note?: string;
  forkWorked?: boolean | null;
  actualMinutes?: number | null;
  reheatedWell?: boolean | null;
  swaps?: string | null;
  wouldCookAgain?: boolean | null;
}

export interface MealMark {
  mealId: number;
  starred: boolean;
  note: string | null;
  forkWorked: boolean | null;
  actualMinutes: number | null;
  reheatedWell: boolean | null;
  swaps: string | null;
  wouldCookAgain: boolean | null;
}

export interface MyReview {
  /** ⛔ False until the household has actually COOKED. Signing up is not an opinion. */
  eligible: boolean;
  /** Why not, in words worth showing — otherwise it becomes a support email. */
  reason: string | null;
  review: {
    rating: number;
    displayName: string;
    headline: string | null;
    body: string;
    status: string;
  } | null;
}

export interface PlanSlot {
  dayIndex: number;
  slot: "breakfast" | "lunch" | "dinner";
  mealName: string | null;
  /** Set on a chosen dinner, so the plan can link to the method (#1324). */
  mealId: number | null;
  /** Needed to log who liked it (#1284). */
  id?: number;
  source: "chosen" | "repeat" | "leftover" | "unsolved";
  sides: string[];
  /** Set for a slow cooker: the morning action. */
  startTime: string | null;
  note: string | null;
}

export interface PlanItem {
  id: number;
  itemName: string;
  aisle: string;
  qtyNote: string | null;
  style: "pack" | "scratch";
  checked: boolean;
  haveAlready: boolean;
  perishable: boolean;
  storedIn: string | null;
  /** One line, several reasons. */
  forMeals: string[];
  /** Empty means everyone; otherwise only these eaters want it. */
  forEaterIds: number[];
}

export interface Plan {
  id: number;
  shopDate: string;
  createdAt: string;
  slots: PlanSlot[];
  items: PlanItem[];
  unsolvedLunches: number[];
  notes: string[];
}

export interface Billing {
  tier: "free" | "plus";
  limits: { eaters: number; planDays: number; restrictionsPerEater: number };
  canAddEater: boolean;
  maxPlanDays: number;
  /** Always true, on every tier. Safety is never a SKU. */
  canRecordAllergy: boolean;
  canRecordMedical: boolean;
  status: string | null;
  currentPeriodEnd: string | null;
  cancelsAtPeriodEnd: boolean;
  prices: { monthly: string; yearly: string };
  configured: boolean;
}

export interface CookStep {
  position: number;
  /** base | pivot | fork — the pivot is where one pot becomes two. */
  role: "base" | "pivot" | "fork";
  group: string | null;
  text: string;
  minutes: number | null;
  phase: "morning" | "evening" | null;
  requirementName: string | null;
  componentName: string;
  /** Named plates, on a fork step. */
  forPlates: string[];
  /** What was ACTUALLY bought for this step. */
  ingredients: string[];
  style: "pack" | "scratch" | null;
}

export interface MethodResponse {
  method: CookMethod;
  /** The note from last time, shown before step one. */
  lastTime: string | null;
  starred: boolean;
}

export interface GatherLine {
  item: string;
  /**
   * Already scaled for this table and rendered — "1½ tsp". Null where no
   * measure has been authored yet, so the line simply shows the item.
   */
  amount: string | null;
  requirement: string;
  style: "pack" | "scratch";
  /** The household's usual style was overridden to dodge a restricted tag. */
  forcedByRestriction: boolean;
}

export interface GatherFork {
  group: string;
  /** Whose plate, by name. */
  forPlates: string[];
  lines: GatherLine[];
}

/** What to get out before step one. Split by whose plate, never one flat list. */
export interface Gather {
  base: GatherLine[];
  forks: GatherFork[];
}

export interface CookMethod {
  mealName: string;
  steps: CookStep[];
  activeMinutes: number;
  slowCooker: boolean;
  morningSteps: CookStep[];
  eveningSteps: CookStep[];
  gather: Gather;
  /** Keyed by component name, so a salad step shows the salad's seasoning. */
  seasoning: Record<string, string>;
}

export interface ParsedIngredient {
  raw: string;
  quantity: string | null;
  item: string;
  /** ⚠️ A GUESS. Confirmed by a person before anything is written (#1286). */
  suggestedGroup: string | null;
  suggestedRole: "base" | "fork";
}

export interface ParsedRecipe {
  title: string;
  servings: string | null;
  activeMinutes: number | null;
  ingredients: ParsedIngredient[];
  steps: string[];
  /** Lines the parser could not place. Shown, never dropped. */
  leftovers: string[];
}

export interface RecipeStep {
  text: string;
  /** base | pivot | fork — the pivot is where one pot becomes two. */
  role: "base" | "pivot" | "fork";
  group?: string | null;
  minutes?: number | null;
}

/** ⚠️ The SAME shape for add, import and edit. One screen, one payload. */
export interface RecipeDraft {
  name: string;
  activeMinutes?: number;
  unattendedMinutes?: number;
  slowCooker?: boolean;
  reheats?: boolean;
  balanceTag?: "protein" | "veg" | "grain" | "fruit";
  ingredients: { item: string; aisle: string; group: string | null; role: "base" | "fork" }[];
  steps?: RecipeStep[];
  sideIds?: number[];
}

export const twoPlates = {
  foodGroups: () => api<{ groups: FoodGroup[]; presets: TastePreset[] }>("/food-groups"),

  household: () => api<{ household: Household | null }>("/household"),

  /** `chosen` re-ranks the remaining cards against what is already picked. */
  deck: (chosen: number[] = []) =>
    api<Deck>(chosen.length ? `/deck?chosen=${chosen.join(",")}` : "/deck"),

  /**
   * Cook something else on that day (#1337).
   *
   * ⚠️ Returns the WHOLE plan, because a swap changes more than one line: the
   * next day's leftover lunch moves with it, and the shopping list is
   * reconciled rather than rebuilt. Ticks survive.
   */
  /**
   * The link that already exists, without minting a new one.
   * ⛔ Never confuse this with `invite()`, which ROTATES the token and kills
   * whatever the household is already using.
   */
  showInvite: (eaterId: number) =>
    api<{ invite: { eaterId: number; eaterName: string; url: string; expiresAt: string } | null }>(
      `/eaters/${eaterId}/invite`,
    ),

  swapSlot: (planId: number, slotId: number, mealId: number) =>
    api<{ plan: Plan }>(`/plans/${planId}/slots/${slotId}`, {
      method: "PATCH",
      body: JSON.stringify({ mealId }),
    }),

  method: (mealId: number) => api<MethodResponse>(`/meals/${mealId}/method`),

  /** Star a meal, or leave a note for next time. "" clears the note. */
  logCooked: (slotId: number, ratings: { eaterId: number; rating: number }[]) =>
    api<{ logged: number }>(`/slots/${slotId}/cooked`, { method: "POST", body: JSON.stringify({ ratings }) }),

  itemPreferences: () => api<{ preferences: ItemPreference[] }>("/item-preferences"),

  saveItemPreference: (body: Partial<ItemPreference> & { itemName: string }) =>
    api<{ preference: ItemPreference }>("/item-preference", { method: "PUT", body: JSON.stringify(body) }),

  stylePreferences: () =>
    api<{ preferences: { requirementName: string; style: "pack" | "scratch" }[] }>("/style-preferences"),

  saveStylePreference: (requirementName: string, style: "pack" | "scratch" | null) =>
    api<{ requirementName: string; style: string | null }>("/style-preference", {
      method: "PUT",
      body: JSON.stringify({ requirementName, style }),
    }),

  /**
   * Star a meal, leave a note, or tell us what the library got wrong.
   *
   * ⛔ `null` CLEARS an answer; leaving a key OUT leaves it alone. They are
   * different things, so a mistaken tap can be undone.
   */
  markMeal: (mealId: number, body: MealMarkInput) =>
    api<{ mark: MealMark }>(`/meals/${mealId}/note`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  /** The household's own review of the app, and whether it has earned one. */
  myReview: () => api<MyReview>("/reviews/mine"),

  saveReview: (body: {
    displayName: string;
    rating: number;
    headline?: string;
    body: string;
  }) =>
    api<{ review: { rating: number; status: string } }>("/reviews/mine", {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  /** The list as plain text (#1293). Fetched when the list opens so a share
   *  sheet can be handed the string inside the tap that asked for it. */
  exportList: (planId: number, includePlan = false) =>
    api<{ text: string }>(`/plans/${planId}/export${includePlan ? "?plan=1" : ""}`),

  /**
   * The most recent plan, and whether its week has turned over.
   *
   * ⚠️ `stale` does NOT mean gone. The list may be half-ticked and tonight's
   * dinner may be cooking from it — the screen leads with "plan this week" and
   * still shows what is there.
   */
  currentPlan: () =>
    api<{ plan: Plan | null; stale?: boolean; weekEndsAt?: string }>("/plans/current"),

  createPlan: (body: { mealIds: number[]; breakfastMealId?: number }) =>
    api<{ plan: Plan }>("/plans", { method: "POST", body: JSON.stringify(body) }),

  tickItem: (planId: number, itemId: number, body: { checked?: boolean; haveAlready?: boolean }) =>
    api<{ item: { id: number; checked: boolean; haveAlready: boolean } }>(
      `/plans/${planId}/items/${itemId}`,
      { method: "PATCH", body: JSON.stringify(body) },
    ),

  createHousehold: (body: {
    name: string;
    shopDay?: number;
    prefersStyle?: "pack" | "scratch";
    timezone?: string;
    eaters: { name: string; colour?: string }[];
  }) => api<{ household: Household }>("/household", { method: "POST", body: JSON.stringify(body) }),

  updateHousehold: (body: {
    name?: string;
    shopDay?: number;
    prefersStyle?: "pack" | "scratch";
    varietyPreference?: "repeat" | "balanced" | "varied";
    planDays?: number;
    timezone?: string;
  }) => api<{ household: Household }>("/household", { method: "PATCH", body: JSON.stringify(body) }),

  parseRecipe: (body: { text?: string; imageBase64?: string }) =>
    api<{ draft: ParsedRecipe; source: "text" | "ocr"; warning: string | null }>("/import/parse", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  commitRecipe: (body: RecipeDraft) =>
    api<{ meal: { id: number; name: string } }>("/import/commit", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** Library sides an imported recipe can be served with (#1337). */
  librarySides: () =>
    api<{ sides: { id: number; name: string; balanceTag: string; activeMinutes: number }[] }>("/sides"),

  loadRecipe: (mealId: number) =>
    api<{ recipe: RecipeDraft & { id: number } }>(`/import/${mealId}`),

  updateRecipe: (mealId: number, body: RecipeDraft) =>
    api<{ meal: { id: number; name: string } }>(`/import/${mealId}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  removeRecipe: (mealId: number) => api<void>(`/import/${mealId}`, { method: "DELETE" }),

  myRecipes: () =>
    api<{ meals: { id: number; name: string; activeMinutes: number; active: boolean }[] }>("/import/mine"),

  cartRetailers: () =>
    api<{ retailers: { key: string; name: string; configured: boolean; note: string }[]; neverOrders: boolean }>(
      "/cart/retailers",
    ),

  prepareCart: (planId: number, retailer: string) =>
    api<{
      retailer: string;
      items: { sku: string; quantity: number; itemName: string }[];
      toConfirm: { itemName: string; candidates: { sku: string; description: string; size: string | null }[] }[];
      notes: string[];
      coverage: { matched: number; total: number };
      configured: boolean;
      message: string | null;
    }>("/cart/prepare", { method: "POST", body: JSON.stringify({ planId, retailer }) }),

  reminders: () =>
    api<{
      configured: boolean;
      publicKey: string | null;
      on: boolean;
      devices: number;
      kinds: { slowCooker: boolean; shopDay: boolean; planEnding: boolean; cookLog: boolean };
    }>("/reminders"),

  updateReminders: (body: Record<string, boolean>) =>
    api<{ on: boolean }>("/reminders", { method: "PATCH", body: JSON.stringify(body) }),

  subscribePush: (body: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
    api<{ subscribed: boolean }>("/reminders/subscribe", { method: "POST", body: JSON.stringify(body) }),

  testReminder: () =>
    api<{ sent: number; pruned: number; skipped: string | null }>("/reminders/test", { method: "POST" }),

  billing: () => api<Billing>("/billing"),

  checkout: (interval: "month" | "year") =>
    api<{ url: string }>("/billing/checkout", { method: "POST", body: JSON.stringify({ interval }) }),

  portal: () => api<{ url: string }>("/billing/portal", { method: "POST" }),

  restrictionTargets: () => api<{ targets: RestrictionTarget[] }>("/restriction-targets"),

  addRestriction: (eaterId: number, body: { target: string; severity: string; note?: string }) =>
    api<{ restriction: Restriction }>(`/eaters/${eaterId}/restrictions`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  removeRestriction: (eaterId: number, restrictionId: number) =>
    api<void>(`/eaters/${eaterId}/restrictions/${restrictionId}`, { method: "DELETE" }),

  invite: (eaterId: number) =>
    api<{ invite: { eaterId: number; eaterName: string; url: string; expiresAt: string } }>(
      `/eaters/${eaterId}/invite`,
      { method: "POST" },
    ),

  revokeInvite: (eaterId: number) => api<void>(`/eaters/${eaterId}/invite`, { method: "DELETE" }),

  /**
   * A recipe's photos and clips.
   *
   * ⛔ THE UPLOAD DOES NOT GO THROUGH OUR SERVER. `presignMedia` returns a URL
   * the browser PUTs the file to directly; `confirmMedia` records it afterwards.
   * A 200MB video routed through the API would hold a worker open for the whole
   * of somebody's upload, on the box that also serves the plan.
   *
   * ⚠️ `available: false` means storage is not configured — not an error. The
   * screen hides its buttons rather than offering something that cannot work.
   */
  mealMedia: (mealId: number) =>
    api<{
      media: Array<{
        id: number; kind: "image" | "video"; caption: string | null;
        contentType: string; bytes: number; createdAt: string;
        mine: boolean; shared: boolean; url: string;
      }>;
      available: boolean;
      canShare?: boolean;
    }>(`/meals/${mealId}/media`),

  presignMedia: (mealId: number, body: { contentType: string; bytes: number }) =>
    api<{ url: string; objectKey: string; kind: "image" | "video" }>(
      `/meals/${mealId}/media/presign`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  confirmMedia: (
    mealId: number,
    body: { objectKey: string; contentType: string; bytes: number; caption?: string; shared?: boolean },
  ) => api<{ media?: { id: number } }>(`/meals/${mealId}/media`, { method: "POST", body: JSON.stringify(body) }),

  deleteMedia: (id: number) => api<{ deleted: boolean }>(`/media/${id}`, { method: "DELETE" }),

  audit: (planId: number) => api<Audit>(`/plans/${planId}/audit`),

  answerAudit: (planId: number, answers: { itemName: string; have: boolean }[]) =>
    api<{ stillToBuy: number; alreadyHave: number }>(`/plans/${planId}/audit`, {
      method: "POST",
      body: JSON.stringify({ answers }),
    }),

  // The tokenised link. No bearer token — the URL IS the credential (#1285).
  link: (token: string) => api<LinkView>(`/link/${token}`, { auth: false }),

  saveLinkTaste: (
    token: string,
    body: { affinities?: Record<string, number>; leftoverTolerance?: number; finished?: boolean },
  ) => api<{ eater: { name: string; answers: Record<string, number>; unanswered: string[] } }>(
    `/link/${token}/taste`,
    { method: "PUT", auth: false, body: JSON.stringify(body) },
  ),

  saveTaste: (
    eaterId: number,
    body: { affinities?: Record<string, number>; leftoverTolerance?: number },
  ) =>
    api<{ eater: Pick<Eater, "id" | "name" | "leftoverTolerance" | "answers" | "unanswered"> }>(
      `/eaters/${eaterId}/taste`,
      { method: "PUT", body: JSON.stringify(body) },
    ),
};

/** 0 refuse · 1 will eat · 2 like · 3 love. */
export const AFFINITY_LABELS: Record<number, string> = {
  3: "Love it",
  2: "Like it",
  1: "Will eat it",
  0: "No",
};

/** 0 never · 1 only if it reheats · 2 gladly. */
export const LEFTOVER_LABELS: Record<number, { title: string; note: string }> = {
  2: { title: "Gladly", note: "Last night's pan is a good lunch." },
  1: { title: "Only if it reheats", note: "Warm, yes. Cold out of the fridge, no." },
  0: { title: "Never", note: "Lunch has to be something else." },
};

/** The walk through a shop, not the alphabet. */
export const AISLE_ORDER = [
  "produce", "meat", "fish", "dairy", "refrigerated", "frozen", "bakery", "pantry",
];

export const AISLE_LABEL: Record<string, string> = {
  produce: "Produce",
  meat: "Meat",
  fish: "Fish",
  dairy: "Dairy",
  refrigerated: "Refrigerated",
  frozen: "Frozen",
  bakery: "Bakery",
  pantry: "Pantry",
};

export const STORAGE_LABEL: Record<string, string> = {
  fridge: "In the fridge",
  freezer: "In the freezer",
  pantry: "In the cupboard",
  spice: "In the spice rack",
};

export const SEVERITY_COPY: Record<string, { name: string; note: string }> = {
  avoid: { name: "I choose not to", note: "A diet, not a danger. Shapes the base of a meal and nothing else." },
  medical: { name: "It makes me ill", note: "Never reaches this plate, base or fork. Others can still have it." },
  allergy: { name: "I am allergic", note: "Blocks the whole meal for everyone — the pan and the spoon are shared." },
};

export const VARIETY_COPY: Record<string, { name: string; note: string }> = {
  repeat: { name: "Lean on repeats", note: "Fewer ingredients, less waste. One cabbage can feed two dinners." },
  balanced: { name: "Balanced", note: "Best meals first, without three versions of the same dinner." },
  varied: { name: "Keep it different", note: "Push the three nights apart, even when it means a longer list." },
};

/** Enough labels for the longest run we allow. */
export const DAY_LABELS = [
  "Day one", "Day two", "Day three", "Day four",
  "Day five", "Day six", "Day seven",
];

/**
 * What the browser thinks the zone is (#1331).
 *
 * Sent at setup so a household is right from its first plan, and offered in
 * Kitchen so an existing one can be corrected in a tap rather than hunted for
 * in a list of six hundred.
 */
export function detectedTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}
